import type {
    ContextMeshAdapter,
    ContextMeshAnalysis,
    ContextMeshAnalysisResult,
    ContextMeshRecommendationDetail,
    ImprovementPathResult,
    ImprovementPathTarget,
    RecommendationEntityRef,
    RecommendationEntityResult
} from "@codenexus/react";

import {
    classifyContextMeshProvenance,
    ContextMeshApiError,
    parseContextMeshAnalysis,
    type ContextMeshAnalysis as IntelligenicContextMeshAnalysis,
    type ContextMeshCategoryAnalysis,
    type ContextMeshCategoryScores,
    type ContextMeshRecommendation
} from "./contextMeshAnalysis";

type ContextMeshAnalysisWithCategories = ContextMeshAnalysis & {
    categoryScores: ContextMeshCategoryScores | null;
    categoryAnalysis: ContextMeshCategoryAnalysis | null;
};

type CurrentContextMeshAnalysis = Extract<ContextMeshAnalysisResult, { status: "ok" }>["analysis"];
type LastReadyContextMeshAnalysis = Extract<ContextMeshAnalysisResult, { status: "stale" }>["analysis"];

const ENTITY_TYPES = new Set<RecommendationEntityRef["type"]>([
    "product",
    "persona",
    "document",
    "documents",
    "feature",
    "user_story",
    "stories",
    "integration",
    "repository"
]);

interface ContextMeshBridgeAdapterOptions {
    apiUrl: string;
    productId: number | null;
    contextMeshId: string | null;
    getToken: () => Promise<string>;
    navigateToView: (view: string) => void;
    openFeatureStudioEntityRef: (href: string) => FeatureStudioEntityOpenResult | boolean | Promise<FeatureStudioEntityOpenResult | boolean>;
    fireSkill: (name: string, inputs?: Record<string, unknown>) => Promise<void>;
    activeWorkProduct: {
        workProductTitle?: string | null;
        workProductLevel?: string | null;
        editorSurface?: string | null;
    } | null;
}

export interface FeatureStudioEntityOpenResult {
    opened: boolean;
    reason?: string;
}

const FEATURE_STUDIO_ENTITY_TYPES = new Set(["product", "feature", "user_story", "stories"]);

function isRecord(value: unknown): value is Record<string, unknown> {
    return typeof value === "object" && value !== null && !Array.isArray(value);
}

function nonEmptyString(value: unknown): string | null {
    return typeof value === "string" && value.trim() ? value.trim() : null;
}

function recommendation(detail: ContextMeshRecommendation): ContextMeshRecommendationDetail {
    const { estimatedScoreDelta, ...rest } = detail;
    // The installed bridge package still declares the legacy field as
    // required. Keep it absent at runtime when the analyzer cannot provide an
    // accurate counterfactual; current bridge consumers already treat it as
    // optional.
    return {
        ...rest,
        ...(typeof estimatedScoreDelta === "number" ? { estimatedScoreDelta } : {}),
        entityRefs: detail.entityRefs
            .filter((ref): ref is typeof ref & { type: RecommendationEntityRef["type"] } =>
                ENTITY_TYPES.has(ref.type as RecommendationEntityRef["type"])
            )
            .map(ref => ({ ...ref, type: ref.type as RecommendationEntityRef["type"] }))
    } as ContextMeshRecommendationDetail;
}

function analysis(
    parsed: IntelligenicContextMeshAnalysis,
    provenance: "current"
): CurrentContextMeshAnalysis & ContextMeshAnalysisWithCategories;
function analysis(
    parsed: IntelligenicContextMeshAnalysis,
    provenance: "last_ready"
): LastReadyContextMeshAnalysis & ContextMeshAnalysisWithCategories;
function analysis(
    parsed: IntelligenicContextMeshAnalysis,
    _provenance: "current" | "last_ready"
): ContextMeshAnalysisWithCategories {
    return {
        productId: parsed.productId,
        tier: parsed.tier,
        score: parsed.score,
        readinessScore: parsed.readinessScore,
        qualityScore: parsed.qualityScore,
        categoryScores: parsed.categoryScores,
        categoryAnalysis: parsed.categoryAnalysis,
        rubricVersion: parsed.rubricVersion,
        tierBasis: {
            candidateTier: parsed.tierBasis.candidateTier,
            semanticFloorPassed: parsed.tierBasis.semanticFloorPassed,
            nextTier: parsed.tierBasis.nextTier ?? undefined,
            blockingGapCodes: parsed.tierBasis.blockingGapCodes
        },
        semanticAnalysis: parsed.semanticAnalysis,
        recommendations: parsed.recommendations,
        recommendationDetails: parsed.recommendationDetails.map(recommendation),
        nextBestAction: parsed.nextBestAction ? recommendation(parsed.nextBestAction) : null
    } as ContextMeshAnalysisWithCategories;
}

async function responsePayload(response: Response): Promise<unknown> {
    try {
        return await response.json();
    } catch {
        throw new Error("The Context Mesh service returned an invalid response.");
    }
}

function baselineResult(payload: unknown): ContextMeshAnalysisResult | null {
    if (!isRecord(payload) || !isRecord(payload.error) || payload.error.code !== "BASELINE_INCOMPLETE") {
        return null;
    }
    return {
        status: "baseline_incomplete",
        message: typeof payload.error.message === "string" ? payload.error.message : undefined,
        missingFields: Array.isArray(payload.error.missingFields)
            ? payload.error.missingFields.filter((field): field is string => typeof field === "string")
            : [],
        recommendations: Array.isArray(payload.error.recommendations)
            ? payload.error.recommendations.filter((item): item is string => typeof item === "string")
            : []
    };
}

function errorMessage(payload: unknown, fallback: string): string {
    if (isRecord(payload)) {
        if (isRecord(payload.error) && typeof payload.error.message === "string") return payload.error.message;
        if (typeof payload.detail === "string") return payload.detail;
    }
    return fallback;
}

function analyzingResult(): ContextMeshAnalysisResult {
    // @codenexus/react adds this scoreless state in the accompanying KAN-1855
    // change. Keep the compatibility assertion at this adapter boundary until
    // Intelligenic can consume that published package version.
    return {
        status: "analyzing",
        message: "Context Mesh is being analyzed. The final score will appear when canonical analysis completes."
    } as unknown as ContextMeshAnalysisResult;
}

export function createIntelligenicContextMeshAdapter(
    options: ContextMeshBridgeAdapterOptions
): ContextMeshAdapter {
    const listeners = new Set<(result: ContextMeshAnalysisResult) => void>();
    let lastResult: ContextMeshAnalysisResult | null = null;

    const publish = (result: ContextMeshAnalysisResult): ContextMeshAnalysisResult => {
        lastResult = result;
        listeners.forEach(listener => listener(result));
        return result;
    };

    const fetchAnalysis = async (): Promise<ContextMeshAnalysisResult> => {
        if (!options.productId && !options.contextMeshId) {
            return publish({ status: "error", message: "Open a Context Mesh to review its health." });
        }

        try {
            const token = await options.getToken();
            if (!token) return publish({ status: "error", message: "Authentication is required." });
            const analysisPath = options.contextMeshId
                ? `/api/context-meshes/${encodeURIComponent(options.contextMeshId)}/context-analysis`
                : `/api/products/${options.productId}/context-analysis`;
            const response = await fetch(
                `${options.apiUrl.replace(/\/$/, "")}${analysisPath}`,
                {
                    headers: {
                        Authorization: `Bearer ${token}`,
                        Accept: "application/json"
                    }
                }
            );
            const payload = await responsePayload(response);
            if (!response.ok) {
                const baseline = response.status === 422 ? baselineResult(payload) : null;
                return publish(
                    baseline ?? {
                        status: "error",
                        message: errorMessage(payload, "Context analysis is temporarily unavailable.")
                    }
                );
            }

            let analysisPayload = payload;
            if (options.contextMeshId) {
                if (!isRecord(payload) || payload.contextMeshId !== options.contextMeshId || !isRecord(payload.analysis)) {
                    throw new Error("The Context Mesh service returned a mismatched analysis.");
                }
                analysisPayload = { ...payload.analysis, productId: 0 };
            }
            const classified = classifyContextMeshProvenance(parseContextMeshAnalysis(analysisPayload));
            if (classified.state === "current_canonical") {
                return publish({ status: "ok", analysis: analysis(classified.analysis, "current") });
            }
            if (classified.state === "prior_canonical") {
                return publish({ status: "stale", analysis: analysis(classified.analysis, "last_ready") });
            }
            return publish(
                classified.state === "analyzing"
                    ? analyzingResult()
                    : { status: "error", message: "Context Mesh analysis is unavailable." }
            );
        } catch (error) {
            const message =
                error instanceof ContextMeshApiError || error instanceof Error
                    ? error.message
                    : "Context analysis is temporarily unavailable.";
            return publish({ status: "error", message });
        }
    };

    const currentAnalysis = async (): Promise<ContextMeshAnalysis | null> => {
        if (!lastResult || (lastResult.status !== "ok" && lastResult.status !== "stale")) {
            await fetchAnalysis();
        }
        return lastResult && (lastResult.status === "ok" || lastResult.status === "stale")
            ? lastResult.analysis
            : null;
    };

    return {
        fetchAnalysis,
        async fetchRecommendationEntity({ recommendationId, type, id }): Promise<RecommendationEntityResult> {
            const current = await currentAnalysis();
            if (!current) return { status: "not_inspectable" };
            const detail = current.recommendationDetails.find(item => {
                if (recommendationId && item.recommendationId !== recommendationId) return false;
                return item.entityRefs.some(ref => ref.type === type && (!id || ref.id === id));
            });
            const entity = detail?.entityRefs.find(ref => ref.type === type && (!id || ref.id === id));
            if (!detail || !entity) return { status: "not_inspectable" };
            return {
                status: "ok",
                label: entity.label,
                href: entity.href,
                recommendationMessage: detail.message,
                recommendationPriority: detail.priority,
                ...(typeof detail.estimatedScoreDelta === "number"
                    ? { recommendationEstimatedScoreDelta: detail.estimatedScoreDelta }
                    : {}),
                recommendationTierImpact: detail.tierImpact,
                recommendationEvidence: detail.evidence
            };
        },
        async openImprovementPath(
            target: ImprovementPathTarget,
            inputs: Record<string, unknown> = {}
        ): Promise<ImprovementPathResult> {
            switch (target) {
                case "file-upload":
                    options.navigateToView("uploads");
                    await options.fireSkill("welcome-uploads", { currentRoute: "uploads", ...inputs });
                    return { status: "opened" };
                case "integrations":
                    options.navigateToView("integrations");
                    await options.fireSkill("welcome-integrations", { currentRoute: "integrations", ...inputs });
                    return { status: "opened" };
                case "feature-studio": {
                    const entityHref = nonEmptyString(inputs.entityHref);
                    const entityType = nonEmptyString(inputs.entityType);
                    const entityId = nonEmptyString(inputs.entityId);
                    const entityLabel = nonEmptyString(inputs.entityLabel);
                    if (!entityHref || !entityType || !entityId || !entityLabel) {
                        return {
                            status: "failed",
                            reason: "Feature Studio navigation requires entityHref, entityType, entityId, and entityLabel."
                        };
                    }
                    if (!FEATURE_STUDIO_ENTITY_TYPES.has(entityType)) {
                        return {
                            status: "failed",
                            reason: `Feature Studio cannot open entity type "${entityType}".`
                        };
                    }
                    try {
                        const outcome = await options.openFeatureStudioEntityRef(entityHref);
                        const opened = typeof outcome === "boolean" ? outcome : outcome.opened;
                        if (opened) return { status: "opened" };
                        return {
                            status: "failed",
                            reason: typeof outcome === "object" && outcome.reason
                                ? outcome.reason
                                : `The Feature Studio link for ${entityType} "${entityLabel}" is invalid or cannot be opened.`
                        };
                    } catch {
                        return {
                            status: "failed",
                            reason: `The Feature Studio link for ${entityType} "${entityLabel}" could not be opened.`
                        };
                    }
                }
                case "work-product-editor":
                    if (!options.activeWorkProduct?.workProductTitle) {
                        return { status: "failed", reason: "Select a work product before opening the editor." };
                    }
                    options.navigateToView("editor");
                    await options.fireSkill("work-product-editor", {
                        workProductName: options.activeWorkProduct.workProductTitle,
                        workProductType: options.activeWorkProduct.workProductLevel ?? undefined,
                        editorMode: options.activeWorkProduct.editorSurface ?? undefined,
                        currentPage: "editor",
                        ...inputs
                    });
                    return { status: "opened" };
                default:
                    return { status: "failed", reason: `Unknown improvement path target: "${String(target)}".` };
            }
        },
        onMeshChange(listener) {
            listeners.add(listener);
            return () => listeners.delete(listener);
        }
    };
}
